import type { Accessor } from "solid-js";
import { createPowerSyncQuery, createPowerSyncSingle } from "@listto/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import type { AttachmentRecord, SubtaskRecord, Todo } from "@listto/powersync";
import {
  getAttachmentsByTodoQuery,
  getSubtasksByTodoQuery,
  getTodoDetailQuery,
  queryKeys,
} from "@listto/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface UseTodoDetailPrefetchOptions {
  /** The todo currently centered in the carousel. */
  todoId: Accessor<string>;
  /** Adjacent todos to warm; `null` when there is no neighbour. */
  prevTodoId: Accessor<string | null>;
  nextTodoId: Accessor<string | null>;
}

/**
 * Data layer of the todo-detail carousel.
 *
 * Warms the PowerSync/TanStack cache for the centered todo (plus its subtasks
 * and attachments) and for the two adjacent todos, so swipe previews are already
 * populated before a gesture starts. Lives in its own hook to keep the carousel
 * component focused on gestures/animation rather than data fetching.
 *
 * Returns the centered todo's query so callers can read its live data (e.g. the
 * drawer "toggle important" shortcut).
 */
export function useTodoDetailPrefetch(options: UseTodoDetailPrefetchOptions) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const isReady = (id: string | null | undefined) => Boolean(id) && Boolean(drizzle());

  const todoQuery = createPowerSyncSingle<Todo>({
    queryKey: () => queryKeys.todos.detail(options.todoId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTodoDetailQuery(dbInstance, options.todoId()) : null;
    },
    enabled: () => isReady(options.todoId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: (prev) => prev,
    suspense: false,
  });

  createPowerSyncQuery<SubtaskRecord>({
    queryKey: () => queryKeys.subtasks.byTodo(options.todoId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getSubtasksByTodoQuery(dbInstance, options.todoId()) : null;
    },
    enabled: () => isReady(options.todoId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  createPowerSyncQuery<AttachmentRecord>({
    queryKey: () => queryKeys.attachments.byTodo(options.todoId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAttachmentsByTodoQuery(dbInstance, options.todoId()) : null;
    },
    enabled: () => isReady(options.todoId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  // Eagerly warm adjacent todos so carousel previews are populated before swipe.
  createPowerSyncSingle<Todo>({
    queryKey: () => queryKeys.todos.detail(options.prevTodoId() ?? ""),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const id = options.prevTodoId();
      return dbInstance && id ? getTodoDetailQuery(dbInstance, id) : null;
    },
    enabled: () => isReady(options.prevTodoId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  createPowerSyncSingle<Todo>({
    queryKey: () => queryKeys.todos.detail(options.nextTodoId() ?? ""),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const id = options.nextTodoId();
      return dbInstance && id ? getTodoDetailQuery(dbInstance, id) : null;
    },
    enabled: () => isReady(options.nextTodoId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  return { todoQuery };
}
